import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Badge, Spinner, Alert, ButtonGroup, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const toResortSlug = (name, id) => {
  const slug = name.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug}-${id.substring(0, 8)}`;
};

const Leaderboard = () => {
  const [resorts, setResorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('forecast');
  const [timeframe, setTimeframe] = useState('next24Hours');
  const [recentTimeframe, setRecentTimeframe] = useState('snowfall24h');

  useEffect(() => {
    const fetchResorts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/ski_areas');
        
        if (!response.ok) {
          throw new Error(`Failed to fetch resort data: ${response.status}`);
        }
        
        const data = await response.json();
        setResorts(data.features || []);
      } catch (error) {
        console.error('Error fetching resort data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResorts();
  }, []);

  const formatSnowfall = useCallback((cm) => {
    if (cm === null || cm === undefined || isNaN(cm)) return '0.0 cm';
    return `${Number(cm).toFixed(1)} cm`;
  }, []);

  const getSnowAmount = useCallback((resort) => {
    if (sortBy === 'forecast') {
      return resort.properties[timeframe] || 0;
    } else {
      const recentSnow = resort.properties.recentSnowfall || resort.properties;
      return recentSnow[recentTimeframe] || 0;
    }
  }, [sortBy, timeframe, recentTimeframe]);

  const topResorts = useMemo(() => {
    return [...resorts]
      .map(resort => ({
        ...resort,
        snowAmount: getSnowAmount(resort)
      }))
      .sort((a, b) => b.snowAmount - a.snowAmount)
      .slice(0, 100);
  }, [resorts, getSnowAmount]);

  const getRankBadgeStyle = useCallback((rank) => {
    if (rank === 1) return { backgroundColor: 'var(--pm-rank-gold)', color: '#0f1923' };
    if (rank === 2) return { backgroundColor: 'var(--pm-rank-silver)', color: '#0f1923' };
    if (rank === 3) return { backgroundColor: 'var(--pm-rank-bronze)', color: '#0f1923' };
    return { backgroundColor: 'var(--pm-bg-elevated)', color: 'var(--pm-text-muted)' };
  }, []);


  const getSnowColor = useCallback((cm) => {
    if (cm === null || cm === undefined || cm === 0) return 'var(--pm-snow-zero)';
    if (cm < 10) return 'var(--pm-snow-light)';
    if (cm < 30) return 'var(--pm-snow-moderate)';
    return 'var(--pm-snow-heavy)';
  }, []);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" size="lg" />
          <div className="mt-3">Loading leaderboard...</div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Leaderboard</Alert.Heading>
          <p>{error}</p>
          <Link to="/" className="btn btn-primary">
            Back to Map
          </Link>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="leaderboard-container" style={{ maxWidth: '1100px', paddingTop: '24px', paddingBottom: '48px' }}>
      {/* Page header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h3 fw-bold mb-1">Snowfall Leaderboard</h1>
          <p className="text-muted small mb-0">Top 100 ski resorts ranked by snowfall</p>
        </div>
        <Link to="/" className="btn btn-outline-primary btn-sm">
          Back to Map
        </Link>
      </div>

      {/* Controls */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="py-3">
          <Row className="align-items-end g-3">
            <Col sm={4}>
              <label className="form-label small text-muted mb-1">Sort By</label>
              <ButtonGroup className="w-100">
                <Button
                  variant={sortBy === 'forecast' ? 'primary' : 'outline-primary'}
                  onClick={() => setSortBy('forecast')}
                  size="sm"
                >
                  Forecast
                </Button>
                <Button
                  variant={sortBy === 'recent' ? 'primary' : 'outline-primary'}
                  onClick={() => setSortBy('recent')}
                  size="sm"
                >
                  Recent
                </Button>
              </ButtonGroup>
            </Col>

            <Col sm={4}>
              <label className="form-label small text-muted mb-1">
                {sortBy === 'forecast' ? 'Forecast Period' : 'Recent Period'}
              </label>
              {sortBy === 'forecast' ? (
                <Form.Select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  size="sm"
                >
                  <option value="next24Hours">Next 24 Hours</option>
                  <option value="next48Hours">Next 48 Hours</option>
                  <option value="next72Hours">Next 72 Hours</option>
                </Form.Select>
              ) : (
                <Form.Select
                  value={recentTimeframe}
                  onChange={(e) => setRecentTimeframe(e.target.value)}
                  size="sm"
                >
                  <option value="snowfall24h">Last 24 Hours</option>
                  <option value="snowfall48h">Last 48 Hours</option>
                  <option value="snowfall72h">Last 72 Hours</option>
                </Form.Select>
              )}
            </Col>

            <Col sm={4}>
              <div className="text-center text-sm-end">
                <span className="text-muted small">Showing </span>
                <span className="fw-bold text-primary">{topResorts.length}</span>
                <span className="text-muted small"> resorts</span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr style={{ backgroundColor: 'var(--pm-bg-elevated)' }}>
                  <th className="ps-4 py-3" style={{ width: '70px' }}>Rank</th>
                  <th className="py-3">Resort</th>
                  <th className="py-3 d-none d-md-table-cell">Location</th>
                  <th className="py-3 text-center d-none d-lg-table-cell">Lifts</th>
                  <th className="py-3 text-center d-none d-lg-table-cell">Summit</th>
                  <th className="py-3 text-end pe-4" style={{ width: '140px' }}>
                    {sortBy === 'forecast' ? 'Forecast' : 'Recent'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {topResorts.map((resort, index) => {
                  const rank = index + 1;
                  const props = resort.properties;

                  return (
                    <tr key={props.id} style={rank <= 3 ? { borderLeft: '3px solid var(--pm-accent)' } : {}}>
                      <td className="ps-4 py-3">
                        <span className="d-inline-flex align-items-center gap-1">
                          <Badge
                            bg=""
                            className="px-2 py-1"
                            style={{ fontSize: '0.8rem', minWidth: '36px', ...getRankBadgeStyle(rank) }}
                          >
                            {rank}
                          </Badge>
                        </span>
                      </td>

                      <td className="py-3">
                        <Link
                          to={`/resort/${toResortSlug(props.name, props.id)}`}
                          className="text-decoration-none fw-semibold text-primary"
                        >
                          {props.name}
                        </Link>
                      </td>

                      <td className="py-3 d-none d-md-table-cell">
                        <span className="text-muted small">
                          {props.region && `${props.region}, `}{props.country}
                        </span>
                      </td>

                      <td className="py-3 text-center d-none d-lg-table-cell">
                        <span className="small text-muted">{props.liftCount || '-'}</span>
                      </td>

                      <td className="py-3 text-center d-none d-lg-table-cell">
                        <span className="small text-muted">
                          {props.maxElevation ? `${props.maxElevation}m` : '-'}
                        </span>
                      </td>

                      <td className="py-3 text-end pe-4">
                        <Badge
                          bg=""
                          className="px-2 py-1"
                          style={{
                            fontSize: '0.85rem',
                            fontFamily: 'var(--pm-font-data)',
                            backgroundColor: resort.snowAmount > 0 ? 'var(--pm-snow-badge-bg)' : 'var(--pm-bg-elevated)',
                            color: getSnowColor(resort.snowAmount),
                            border: `1px solid ${getSnowColor(resort.snowAmount)}`,
                          }}
                        >
                          {formatSnowfall(resort.snowAmount)}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <div className="text-center text-muted small mt-3">
        Data shows the top 100 ski resorts by {sortBy === 'forecast' ? 'forecasted' : 'recent'} snowfall.
        Click on any resort name to view detailed information.
      </div>
    </Container>
  );
};

export default Leaderboard;